import React, { useState, useEffect } from 'react';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';
import { apiClient, type User } from '../../lib/api';

interface EditUserModalProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  user,
  onClose,
  onSuccess
}) => {
  const [role, setRole] = useState<'admin' | 'editor'>('editor');
  const [isActive, setIsActive] = useState(true);
  const [status, setStatus] = useState<'pending' | 'active' | 'inactive'>('active');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form when user changes
  useEffect(() => {
    if (user) {
      setRole(user.role);
      setIsActive(user.isActive);
      setStatus(user.status);
      setError(null);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError(null);
    setLoading(true);

    try {
      await apiClient.updateUser(user.id, {
        role,
        isActive,
        status
      });
      
      // Update the user object and notify parent
      const updatedUser: User = {
        ...user,
        role,
        isActive,
        status
      };
      
      onSuccess(updatedUser);
    } catch (err: any) {
      console.error('Failed to update user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!open || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-9 flex items-center">
            <EditOutlined className="mr-2" />
            Edit User
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* User Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                <EditOutlined className="text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-gray-9">{user.email}</p>
                <p className="text-sm text-gray-6">ID: {user.id.substring(0, 8)}...</p>
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-7 mb-2">
              Role
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="editor"
                  checked={role === 'editor'}
                  onChange={(e) => setRole(e.target.value as 'editor')}
                  className="mr-2"
                  disabled={loading}
                />
                <div>
                  <span className="font-medium text-gray-9">Editor</span>
                  <p className="text-sm text-gray-6">Can manage content but not users</p>
                </div>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="admin"
                  checked={role === 'admin'}
                  onChange={(e) => setRole(e.target.value as 'admin')}
                  className="mr-2"
                  disabled={loading}
                />
                <div>
                  <span className="font-medium text-gray-9">Admin</span>
                  <p className="text-sm text-gray-6">Full access to content and user management</p>
                </div>
              </label>
            </div>
          </div>

          {/* Status Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-7 mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'pending' | 'active' | 'inactive')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
              disabled={loading}
            >
              <option value="pending">Pending (waiting for setup)</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Active Toggle */}
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="mr-2"
                disabled={loading}
              />
              <div>
                <span className="font-medium text-gray-9">Account Active</span>
                <p className="text-sm text-gray-6">
                  {isActive ? 'User can log in and access the system' : 'User cannot log in'}
                </p>
              </div>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <EditOutlined className="mr-2" />
                  Update User
                </>
              )}
            </button>
          </div>
        </form>

        {/* Warning for sensitive actions */}
        {(user.role === 'admin' && role !== 'admin') && (
          <div className="px-6 pb-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-yellow-800 text-sm">
                ⚠️ You are changing an admin user to editor. This will remove their user management privileges.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditUserModal;
